import type { Insight } from '../../../hooks/useFinancialInsights';
import type { Goal } from '../../../hooks/useGoals';

interface InsightsSectionProps {
  insights: Insight[];
  loading: boolean;
  monthIncome: number;
  monthExpenses: number;
  prevMonthExpenses: number;
  savingsRate: number | null;
  goals: Goal[];
  onGoalClick?: (goalId: string) => void;
}

const INSIGHT_STYLE: Record<string, { bg: string; border: string; iconColor: string; titleColor: string }> = {
  warning: { bg: 'bg-amber-50', border: 'border-amber-100', iconColor: 'text-amber-500', titleColor: 'text-amber-800' },
  success: { bg: 'bg-emerald-50', border: 'border-emerald-100', iconColor: 'text-emerald-500', titleColor: 'text-emerald-800' },
  info: { bg: 'bg-gray-50', border: 'border-gray-100', iconColor: 'text-gray-500', titleColor: 'text-gray-800' },
  tip: { bg: 'bg-sky-50', border: 'border-sky-100', iconColor: 'text-sky-500', titleColor: 'text-sky-800' },
};

const fmt = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

export const InsightsSection = ({
  insights, loading, monthIncome, monthExpenses,
  prevMonthExpenses, savingsRate, goals, onGoalClick,
}: InsightsSectionProps) => {
  const savings = monthIncome - monthExpenses;
  const expenseChange = prevMonthExpenses > 0
    ? Math.round(((monthExpenses - prevMonthExpenses) / prevMonthExpenses) * 100)
    : null;

  const activeGoals = goals.filter(g => g.status === 'active' && !g.is_completed);

  return (
    <div className="space-y-6">
      {/* Overview cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl border border-gray-100 p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 bg-emerald-50 rounded-xl flex items-center justify-center">
              <i className="ri-arrow-down-circle-line text-emerald-500 text-sm" />
            </div>
            <p className="text-xs text-gray-500 font-500">Receitas no mês</p>
          </div>
          <p className="text-base font-700 text-gray-900">{fmt(monthIncome)}</p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 bg-red-50 rounded-xl flex items-center justify-center">
              <i className="ri-arrow-up-circle-line text-red-400 text-sm" />
            </div>
            <p className="text-xs text-gray-500 font-500">Gastos no mês</p>
          </div>
          <p className="text-base font-700 text-gray-900">{fmt(monthExpenses)}</p>
          {expenseChange !== null && (
            <p className={`text-xs mt-0.5 font-500 ${expenseChange > 0 ? 'text-red-500' : 'text-emerald-500'}`}>
              {expenseChange > 0 ? '+' : ''}{expenseChange}% vs mês anterior
            </p>
          )}
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${savings >= 0 ? 'bg-emerald-50' : 'bg-red-50'}`}>
              <i className={`ri-money-dollar-circle-line text-sm ${savings >= 0 ? 'text-emerald-500' : 'text-red-400'}`} />
            </div>
            <p className="text-xs text-gray-500 font-500">Saldo do mês</p>
          </div>
          <p className={`text-base font-700 ${savings >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>{fmt(savings)}</p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 bg-gray-50 rounded-xl flex items-center justify-center">
              <i className="ri-percent-line text-gray-500 text-sm" />
            </div>
            <p className="text-xs text-gray-500 font-500">Taxa de poupança</p>
          </div>
          <p className={`text-base font-700 ${(savingsRate ?? 0) >= 20 ? 'text-emerald-600' : (savingsRate ?? 0) >= 0 ? 'text-amber-600' : 'text-red-500'}`}>
            {savingsRate !== null ? `${savingsRate.toFixed(0)}%` : '—'}
          </p>
        </div>
      </div>

      {/* Active goals quick overview */}
      {activeGoals.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <p className="text-sm font-700 text-gray-800 mb-3">Resumo das metas ativas</p>
          <div className="space-y-3">
            {activeGoals.map(goal => {
              const needed = goal.target_date
                ? (() => {
                    const today = new Date();
                    const target = new Date(goal.target_date + 'T12:00:00');
                    const months = Math.max(0.5, (target.getFullYear() - today.getFullYear()) * 12 + (target.getMonth() - today.getMonth()));
                    const rem = Math.max(0, goal.target_amount - goal.current_amount);
                    return rem / months;
                  })()
                : null;
              const pct = goal.target_amount > 0 ? Math.min(100, (goal.current_amount / goal.target_amount) * 100) : 0;
              const canAfford = needed !== null && savings > 0 && savings >= needed;

              return (
                <div
                  key={goal.id}
                  className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 -mx-2 px-2 py-1.5 rounded-xl transition-colors"
                  onClick={() => onGoalClick?.(goal.id)}
                >
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: goal.color + '20' }}>
                    <i className={`${goal.icon} text-xs`} style={{ color: goal.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-xs font-600 text-gray-700 truncate">{goal.name}</p>
                      <span className="text-xs font-700 ml-2 flex-shrink-0" style={{ color: goal.color }}>{pct.toFixed(0)}%</span>
                    </div>
                    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: goal.color }} />
                    </div>
                  </div>
                  {needed && (
                    <div className="text-right flex-shrink-0">
                      <p className="text-xs text-gray-400">{fmt(needed)}/mês</p>
                      <p className={`text-xs font-600 ${canAfford ? 'text-emerald-500' : 'text-red-400'}`}>
                        {canAfford ? 'Possível' : 'Ajuste necessário'}
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Insights list */}
      <div>
        <p className="text-sm font-700 text-gray-800 mb-3">Análises automáticas</p>
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => <div key={i} className="h-20 bg-gray-100 rounded-2xl animate-pulse" />)}
          </div>
        ) : insights.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center bg-white rounded-2xl border border-gray-100">
            <div className="w-14 h-14 bg-gray-100 rounded-2xl flex items-center justify-center mb-4">
              <i className="ri-bar-chart-2-line text-2xl text-gray-400" />
            </div>
            <p className="text-base font-700 text-gray-700 mb-2">Ainda sem dados para análise</p>
            <p className="text-sm text-gray-400 mb-6 max-w-sm leading-relaxed">
              Os insights aparecem automaticamente conforme você registra lançamentos e cria metas. Quanto mais você usar o sistema, mais personalizadas ficam as recomendações.
            </p>
            <div className="space-y-2 w-full max-w-xs text-left">
              {[
                { icon: 'ri-focus-3-line', color: 'text-emerald-600 bg-emerald-50', text: 'Crie pelo menos uma meta financeira' },
                { icon: 'ri-exchange-funds-line', color: 'text-gray-600 bg-gray-100', text: 'Registre receitas e despesas em Lançamentos' },
                { icon: 'ri-lightbulb-line', color: 'text-amber-600 bg-amber-50', text: 'Os insights surgem automaticamente' },
              ].map((step, i) => (
                <div key={i} className="flex items-center gap-3 px-4 py-3 bg-gray-50 rounded-xl">
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${step.color}`}>
                    <i className={`${step.icon} text-sm`} />
                  </div>
                  <p className="text-sm text-gray-600">{step.text}</p>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {insights.map(insight => {
              const style = INSIGHT_STYLE[insight.type];
              return (
                <div
                  key={insight.id}
                  className={`flex items-start gap-4 p-4 rounded-2xl border ${style.bg} ${style.border} ${insight.relatedGoalId ? 'cursor-pointer hover:opacity-90 transition-opacity' : ''}`}
                  onClick={() => insight.relatedGoalId && onGoalClick?.(insight.relatedGoalId)}
                >
                  <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 bg-white/60`}>
                    <i className={`${insight.icon} text-sm ${style.iconColor}`} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className={`text-sm font-700 mb-0.5 ${style.titleColor}`}>{insight.title}</p>
                    <p className="text-xs text-gray-600 leading-relaxed">{insight.message}</p>
                  </div>
                  {insight.relatedGoalId && (
                    <i className="ri-arrow-right-s-line text-gray-400 flex-shrink-0 mt-1" />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
